import { useEffect, useState } from "react";
import { fetchPrices, createCheckout, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Section } from "@/components/CalcUI";
import { Check, Zap, Users, Infinity as InfIcon, Lock, BookOpen } from "lucide-react";
import { toast } from "sonner";
import { useLocation } from "react-router-dom";
import { PromoBanner } from "@/components/PromoBanner";

const PLAN_FEATURES = {
  pro: [
    "All calculators (Pressure, Kill Sheet, Tubing, Free Point, Collapse/Burst, Mud Weight)",
    "AI assistant (Claude Sonnet 5) — explains formulas & safety notes",
    "Unlimited saved jobs & history",
    "Field + Metric unit toggle",
    "PDF export of calculations",
  ],
  team: [
    "Everything in Pro",
    "Shared job library across your crew",
    "Team admin dashboard & multi-seat billing",
    "Priority AI rate limits",
    "Company logo on exported reports",
  ],
  lifetime: [
    "Everything in Pro, forever",
    "One-time payment — no subscription",
    "All future updates included",
    "Perfect for independent operators",
  ],
};

function HeroCountdown({ target, launchPrice, regularPrice }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  const diff = new Date(target).getTime() - now;
  if (diff <= 0) return null;
  const d = Math.floor(diff / 86400000);
  const h = Math.floor((diff % 86400000) / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  const s = Math.floor((diff % 60000) / 1000);
  const savings = (regularPrice - launchPrice).toFixed(2);

  const Box = ({ n, label }) => (
    <div className="flex flex-col items-center bg-[#0b0e13] border border-[#f59e0b]/40 px-3 md:px-5 py-2 md:py-3 min-w-[56px] md:min-w-[80px]">
      <span data-testid={`countdown-${label.toLowerCase()}`} className="mono text-2xl md:text-4xl font-bold text-[#f59e0b] tabular-nums leading-none">
        {String(n).padStart(2, "0")}
      </span>
      <span className="mono text-[9px] md:text-[10px] tracking-widest text-[#94a3b8] uppercase mt-1">{label}</span>
    </div>
  );

  return (
    <div
      data-testid="pricing-hero-countdown"
      className="border-2 border-[#f59e0b] bg-gradient-to-br from-[#f59e0b]/15 via-[#11151c] to-[#11151c] p-5 md:p-8 mb-8"
    >
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-5">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Zap className="w-5 h-5 text-[#f59e0b]" strokeWidth={2.5} />
            <span className="mono text-[11px] md:text-xs tracking-widest text-[#f59e0b] font-bold uppercase">
              Founding Rig Offer · Ends Soon
            </span>
          </div>
          <h2 className="heading text-2xl md:text-3xl font-bold text-[#f8fafc] leading-tight">
            Lock in <span className="text-[#f59e0b]">${launchPrice}</span> lifetime access
          </h2>
          <p className="text-sm text-[#94a3b8] mt-1.5">
            Reverts to <span className="line-through">${regularPrice}</span> —
            save <span className="mono text-[#fbbf24] font-bold">${savings}</span> forever
          </p>
        </div>
        <div className="flex items-center gap-2 md:gap-3 justify-center md:justify-end">
          <Box n={d} label="Days" />
          <Box n={h} label="Hours" />
          <Box n={m} label="Mins" />
          <Box n={s} label="Secs" />
        </div>
      </div>
    </div>
  );
}

export default function Pricing() {
  const [prices, setPrices] = useState(null);
  const [interval, setInterval] = useState("month");
  const { user, isAuthed, hasPro, hasGuides, setShowAuth } = useAuth();
  const [busy, setBusy] = useState(null);
  const location = useLocation();
  const proRequired = location.state?.reason === "pro_required";
  const fromPath = location.state?.from;

  useEffect(() => {
    fetchPrices().then(setPrices).catch(() => {});
  }, []);

  const priceMap = (prices?.items || []).reduce((acc, p) => ({ ...acc, [p.lookup_key]: p }), {});
  const launchEnd = prices?.launch_end_date;
  const launchActive = launchEnd && new Date(launchEnd) > new Date();

  const proKey = interval === "month" ? "pro_monthly" : "pro_annual";
  const teamKey = interval === "month" ? "team_monthly" : "team_annual";
  const lifetimeKey = launchActive ? "lifetime_launch" : "lifetime_regular";
  const proSale = prices?.pro_sale;
  const proSaleOn = Boolean(proSale?.active && proKey === proSale.lookup_key);
  const guidesKey = interval === "month" ? "guides_monthly" : "guides_annual";
  const guidesPrice = priceMap[guidesKey];

  const goCheckout = async (key) => {
    if (busy) return; // guard against rapid double-clicks opening orphan tabs
    if (user === null) {
      toast.info("Loading your account — try again in a second");
      return;
    }
    if (!isAuthed) {
      setShowAuth(true);
      toast.info("Sign in first — takes 20 seconds");
      return;
    }
    setBusy(key);
    // Same-tab redirect: reliable on iOS Safari (no popup blocker).
    try {
      const { checkout_url } = await createCheckout(key);
      try {
        window.top.location.href = checkout_url;
      } catch {
        window.location.href = checkout_url;
      }
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Checkout failed — please try again");
      setBusy(null);
    }
  };

  const proPrice = priceMap[proKey];
  const teamPrice = priceMap[teamKey];
  const lifetimePrice = priceMap[lifetimeKey];
  const regularLifetime = priceMap["lifetime_regular"];

  return (
    <div data-testid="pricing-page">
      <PromoBanner className="mb-6" />
      {proRequired && (
        <div
          data-testid="pro-required-banner"
          className="border border-[#ef4444] bg-[#ef4444]/10 px-5 py-3 mb-6 flex items-center gap-3"
        >
          <Lock className="w-5 h-5 text-[#ef4444]" />
          <div>
            <div className="mono text-xs tracking-widest text-[#ef4444]">SUBSCRIPTION REQUIRED</div>
            <div className="text-sm text-[#f8fafc] mt-0.5">
              {fromPath ? <>Access to <span className="mono text-[#f59e0b]">{fromPath}</span> requires a paid plan.</> : "All calculators require a paid plan."} Pick a plan below to unlock everything.
            </div>
          </div>
        </div>
      )}
      {launchActive && (
        <HeroCountdown
          target={launchEnd}
          launchPrice={lifetimePrice?.amount ?? 299.99}
          regularPrice={regularLifetime?.amount ?? 350}
        />
      )}

      <Section
        title="Pricing"
        subtitle="Pick the plan that matches how you work. All plans include every calculator, every formula, every unit system."
      >
        {/* Interval toggle */}
        <div className="inline-flex border border-[#334155] mb-6">
          <button
            data-testid="int-monthly"
            onClick={() => setInterval("month")}
            className={`px-4 py-2 text-xs mono tracking-widest uppercase transition-colors ${
              interval === "month" ? "bg-[#f59e0b] text-[#0b0e13]" : "text-[#94a3b8] hover:text-[#f8fafc]"
            }`}
          >
            Monthly
          </button>
          <button
            data-testid="int-annual"
            onClick={() => setInterval("year")}
            className={`px-4 py-2 text-xs mono tracking-widest uppercase transition-colors ${
              interval === "year" ? "bg-[#f59e0b] text-[#0b0e13]" : "text-[#94a3b8] hover:text-[#f8fafc]"
            }`}
          >
            Annual · 2 mo free
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          {/* PRO */}
          <div data-testid="plan-pro" className={`border ${proSaleOn ? "border-[#f59e0b]" : "border-[#334155]"} bg-[#11151c] p-6 flex flex-col relative`}>
            {proSaleOn && (
              <div data-testid="pro-sale-badge" className="absolute top-0 right-0 bg-[#f59e0b] text-[10px] mono tracking-widest px-2 py-1 text-[#0b0e13] font-bold">
                NOVEMBER DEAL
              </div>
            )}
            <div className="flex items-center gap-2 mb-3">
              <Zap className="w-4 h-4 text-[#f59e0b]" />
              <h3 className="heading text-lg font-bold uppercase tracking-wide">Pro</h3>
            </div>
            <div className="mb-4">
              <span data-testid="pro-price" className="mono text-4xl font-bold text-[#f8fafc]">${proSaleOn ? proSale.sale_price : proPrice?.amount ?? "—"}</span>
              {proSaleOn && (
                <span data-testid="pro-regular-price" className="mono text-sm text-[#64748b] line-through ml-2">${proPrice?.amount}</span>
              )}
              <span className="mono text-sm text-[#94a3b8] ml-2">/{interval === "month" ? "mo" : "yr"}</span>
              {proSaleOn && (
                <p data-testid="pro-sale-note" className="mono text-[11px] text-[#94a3b8] mt-2">
                  First month at ${proSale.sale_price} · November renewals also ${proSale.sale_price}
                </p>
              )}
            </div>
            <ul className="space-y-2 flex-1">
              {PLAN_FEATURES.pro.map((f) => (
                <li key={f} className="flex gap-2 text-sm text-[#f8fafc]">
                  <Check className="w-4 h-4 text-[#f59e0b] shrink-0 mt-0.5" /> {f}
                </li>
              ))}
            </ul>
            <button
              data-testid="buy-pro-btn"
              onClick={() => goCheckout(proKey)}
              disabled={busy === proKey}
              className="mt-6 bg-[#f59e0b] hover:bg-[#fbbf24] text-[#0b0e13] py-3 mono text-xs tracking-widest uppercase font-bold disabled:opacity-40"
            >
              {busy === proKey ? "..." : "Start Pro"}
            </button>
          </div>

          {/* TEAM */}
          <div data-testid="plan-team" className="border border-[#334155] bg-[#11151c] p-6 flex flex-col relative">
            <div className="absolute top-0 right-0 bg-[#334155] text-[10px] mono tracking-widest px-2 py-1 text-[#f8fafc]">
              CREWS
            </div>
            <div className="flex items-center gap-2 mb-3">
              <Users className="w-4 h-4 text-[#f59e0b]" />
              <h3 className="heading text-lg font-bold uppercase tracking-wide">Team</h3>
            </div>
            <div className="mb-4">
              <span className="mono text-4xl font-bold text-[#f8fafc]">${teamPrice?.amount ?? "—"}</span>
              <span className="mono text-sm text-[#94a3b8] ml-2">/{interval === "month" ? "mo" : "yr"}</span>
            </div>
            <ul className="space-y-2 flex-1">
              {PLAN_FEATURES.team.map((f) => (
                <li key={f} className="flex gap-2 text-sm text-[#f8fafc]">
                  <Check className="w-4 h-4 text-[#f59e0b] shrink-0 mt-0.5" /> {f}
                </li>
              ))}
            </ul>
            <button
              data-testid="buy-team-btn"
              onClick={() => goCheckout(teamKey)}
              disabled={busy === teamKey}
              className="mt-6 bg-[#f59e0b] hover:bg-[#fbbf24] text-[#0b0e13] py-3 mono text-xs tracking-widest uppercase font-bold disabled:opacity-40"
            >
              {busy === teamKey ? "..." : "Start Team"}
            </button>
          </div>

          {/* LIFETIME */}
          <div data-testid="plan-lifetime" className={`border ${launchActive ? "border-[#f59e0b]" : "border-[#334155]"} bg-[#11151c] p-6 flex flex-col relative`}>
            {launchActive && (
              <div className="absolute top-0 right-0 bg-[#f59e0b] text-[10px] mono tracking-widest px-2 py-1 text-[#0b0e13] font-bold">
                LAUNCH · ${((regularLifetime?.amount || 0) - (lifetimePrice?.amount || 0)).toFixed(2)} OFF
              </div>
            )}
            <div className="flex items-center gap-2 mb-3">
              <InfIcon className="w-4 h-4 text-[#f59e0b]" />
              <h3 className="heading text-lg font-bold uppercase tracking-wide">Lifetime</h3>
            </div>
            <div className="mb-4">
              <span className="mono text-4xl font-bold text-[#f8fafc]">${lifetimePrice?.amount ?? "—"}</span>
              {launchActive && (
                <span className="mono text-sm text-[#64748b] line-through ml-2">${regularLifetime?.amount}</span>
              )}
              <div className="text-xs mono text-[#94a3b8] mt-1">One-time payment</div>
            </div>
            <ul className="space-y-2 flex-1">
              {PLAN_FEATURES.lifetime.map((f) => (
                <li key={f} className="flex gap-2 text-sm text-[#f8fafc]">
                  <Check className="w-4 h-4 text-[#f59e0b] shrink-0 mt-0.5" /> {f}
                </li>
              ))}
            </ul>
            <button
              data-testid="buy-lifetime-btn"
              onClick={() => goCheckout(lifetimeKey)}
              disabled={busy === lifetimeKey}
              className="mt-6 bg-[#f59e0b] hover:bg-[#fbbf24] text-[#0b0e13] py-3 mono text-xs tracking-widest uppercase font-bold disabled:opacity-40"
            >
              {busy === lifetimeKey ? "..." : "Buy Lifetime"}
            </button>
          </div>
        </div>

        <div data-testid="plan-guides" className="mt-5 border border-[#334155] bg-[#11151c] p-6 flex flex-col md:flex-row md:items-center justify-between gap-5">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <BookOpen className="w-4 h-4 text-[#f59e0b]" />
              <h3 className="heading text-lg font-bold uppercase tracking-wide">Formula Guides</h3>
            </div>
            <p className="text-sm text-[#94a3b8] max-w-xl">
              Every formula and worked example behind RIGCALC's calculators, unlocked. Calculators not included. Already in Pro, Team and Lifetime.
            </p>
          </div>
          <div className="flex items-center gap-5">
            <div>
              <span data-testid="guides-price" className="mono text-3xl font-bold text-[#f8fafc]">${guidesPrice?.amount ?? "—"}</span>
              <span className="mono text-sm text-[#94a3b8] ml-2">/{interval === "month" ? "mo" : "yr"}</span>
            </div>
            {hasGuides ? (
              <span data-testid="guides-included" className="mono text-xs tracking-widest uppercase text-[#10b981] border border-[#10b981]/50 px-3 py-2">{hasPro ? "Included" : "Active"}</span>
            ) : (
              <button
                data-testid="buy-guides-btn"
                onClick={() => goCheckout(guidesKey)}
                disabled={busy === guidesKey}
                className="border border-[#f59e0b] text-[#f59e0b] hover:bg-[#f59e0b] hover:text-[#0b0e13] px-5 py-3 mono text-xs tracking-widest uppercase font-bold disabled:opacity-40 transition-colors"
              >
                {busy === guidesKey ? "..." : "Get Guides"}
              </button>
            )}
          </div>
        </div>

        <div className="mt-8 border border-[#334155] bg-[#11151c] p-5">
          <div className="text-xs mono tracking-widest text-[#f59e0b] mb-2">MONEY-BACK GUARANTEE</div>
          <p className="text-sm text-[#94a3b8]">
            Not the right fit? Cancel any subscription within 30 days for a full refund — no questions asked. Lifetime plans are refundable within 14 days.
          </p>
        </div>
      </Section>
    </div>
  );
}
